import { StrictMode } from 'react';
import { BrowserRouter, matchRoutes } from 'react-router-dom';
import type { OnRenderClientAsync } from 'vike/types';
import { hydrateRoot, createRoot, type Root } from 'react-dom/client';
import { PageShell } from './PageShell';
import { resolveBasePath } from '../src/router/basePath';
import routes, { type VikePageDescriptor } from '../src/router/config';

let root: Root | undefined;
const loggedRecoverableErrors = new Set<string>();

export const onRenderClient: OnRenderClientAsync = async (pageContext) => {
  const { Page, pageProps } = pageContext;
  const container = document.getElementById('page-view');

  if (!container) {
    throw new Error('Vike container element "#page-view" not found');
  }

  const basePath = resolveBasePath();
  const derivePathname = () => {
    if (typeof pageContext.urlOriginal === 'string') {
      try {
        const url = new URL(pageContext.urlOriginal, window.location.origin);
        return url.pathname;
      } catch {
        return pageContext.urlOriginal;
      }
    }

    return window.location.pathname;
  };

  const pathname = derivePathname();
  const matches =
    matchRoutes(routes, { pathname }, basePath === '/' ? undefined : basePath) ?? [];
  const lastMatch = matches.length > 0 ? matches[matches.length - 1] : undefined;
  const matchedDescriptor = (lastMatch?.route.handle?.vike as VikePageDescriptor | undefined) ?? undefined;

  if (matchedDescriptor && !matchedDescriptor.clientComponent) {
    try {
      const module = await matchedDescriptor.load();
      matchedDescriptor.clientComponent = module.default;
    } catch (error) {
      console.warn('Failed to eagerly load client component for', matchedDescriptor.path, error);
    }
  }

  const page = (
    <StrictMode>
      <BrowserRouter basename={resolveBasePath()}>
        <PageShell>
          <Page {...pageProps} />
        </PageShell>
      </BrowserRouter>
    </StrictMode>
  );

  if (pageContext.isHydration) {
    root = hydrateRoot(container, page, {
      onRecoverableError(error, info) {
        const message = typeof error?.message === 'string' ? error.message : String(error);
        const key = `${message}:${info?.componentStack ?? ''}`;

        if (/React error #418/u.test(message)) {
          if (!loggedRecoverableErrors.has(key)) {
            loggedRecoverableErrors.add(key);

            if (process.env.NODE_ENV !== 'production') {
              console.warn('Hydration mismatch detected; React will sync on the client.', error, info);
            }
          }

          return;
        }

        if (process.env.NODE_ENV !== 'production') {
          console.error('Hydration recoverable error:', error, info);
        } else {
          console.error('Hydration recoverable error:', error);
        }
      },
    });
    return;
  }

  if (!root) {
    root = createRoot(container);
  }

  root.render(page);
};
